/**
 * The player record screen. Everything inside #careerRoot, talking to
 * /api/career.
 *
 * It reads like a profile page and not a form dump: who they are, then the
 * shooting this app measured, then the teams, milestones and accolades on one
 * spine, then the games they logged by hand, then NIL, then who it is shared
 * with.
 *
 * Two things this screen will not do.
 *
 * 1. It never makes a number up. Every figure on it comes from the Worker,
 *    which reads real tracked sessions and real logged records. A period with
 *    nothing in it says so. There is no pace, no projection and no estimate
 *    anywhere, and the two example sessions a new account starts with are
 *    already out of every total before it gets here.
 * 2. It never grades anybody. Measured numbers carry a blue MEASURED tag and
 *    self reported ones a purple SELF REPORTED tag, everywhere they appear, so
 *    a coach reading this always knows which is which. There is no overall
 *    rating, no ranking and nothing about what comes next.
 */

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const BASE_TABS = [['career', 'Career timeline'], ['games', 'Games'], ['nil', 'NIL'], ['share', 'Sharing']];

/** Fallback copy, used only if the Worker ever answers without its own. */
const COPY = {
  measured: 'Measured in Hooper Buddy. Every shot here was tracked live in a session on this app.',
  logged: 'Self reported. You or whoever logs for you typed these in, and nothing here checks them.',
  record: 'This page is a record of what was logged. It is not a scouting report, not a grade, and it does not say anything about what comes next.',
  nil: 'A record you keep of deals you have made, in your own words. There is no amount field and no terms field on purpose. Hooper Buddy gives no legal or financial advice and nothing here is a contract.',
  demo: 'The two example sessions every new account starts with are left out of every number on this page, so a new record opens at nothing instead of at twenty seven shots nobody took.',
  sharing: 'There is no public link to this page. A coach or a parent reads it only through a team you joined, and only while your sharing with that team covers your record. You turn that on and off in Settings.',
};

const n0 = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };
const fmt = (v) => Math.round(n0(v)).toLocaleString();
const round1 = (n) => Math.round(n * 10) / 10;
const fmt1 = (v) => (Math.abs(v - Math.round(v)) < 0.05 ? String(Math.round(v)) : String(round1(v)));
const cap = (s) => String(s || '').charAt(0).toUpperCase() + String(s || '').slice(1);
const d2 = (n) => String(n).padStart(2, '0');
const localDay = (d) => `${d.getFullYear()}-${d2(d.getMonth() + 1)}-${d2(d.getDate())}`;
const todayKey = () => localDay(new Date());

/** A YYYY-MM-DD the player typed, read as that day and not as UTC midnight. */
function dateOf(key) {
  const [y, m, d] = String(key || '').slice(0, 10).split('-').map(Number);
  return y ? new Date(y, (m || 1) - 1, d || 1) : null;
}
function fmtDay(key, opts) {
  const d = dateOf(key);
  return d ? d.toLocaleDateString(undefined, opts || { year: 'numeric', month: 'short', day: 'numeric' }) : '';
}
const shortDay = (key) => fmtDay(key, { month: 'short', day: 'numeric' });
function dayLabel(key) {
  if (!key) return '';
  if (key === todayKey()) return 'Today';
  const y = new Date(); y.setDate(y.getDate() - 1);
  if (key === localDay(y)) return 'Yesterday';
  return fmtDay(key);
}
/** An ISO timestamp from a tracked session, shown as its local day. */
const dayOfIso = (iso) => { const t = Date.parse(iso); return Number.isFinite(t) ? localDay(new Date(t)) : ''; };

/* ------------------------------------------------------------------ state */

let ctx = null, root = null, panel = null, tabsEl = null;
let ratingsMod = null;                       // worker ratings UI, when that module exists
let escBound = false;                        // the one document level listener this screen owns
const S = {
  tab: 'career',
  data: null, err: null,
  sort: { key: 'at', dir: -1 },
  orgs: null, orgsErr: null,
};

const data = () => S.data || {};
const copy = (key) => data().copy?.[key] || COPY[key] || '';
const levels = () => (Array.isArray(data().levels) ? data().levels : []);
const levelLabel = (id) => levels().find((l) => l.id === id)?.label || (id ? cap(String(id).replace(/-/g, ' ')) : '');
const ranges = () => data().ranges || {};
const tracked = () => data().totals?.tracked || {};
const gameTotals = () => data().totals?.games || {};
const rows = (key) => (Array.isArray(data()[key]) ? data()[key].filter(Boolean) : []);

/* ------------------------------------------------------------------- boot */

export function mount(c) {
  ctx = c;
  root = document.getElementById('careerRoot');
  if (!root) return;
  root.innerHTML = `
    <div id="crHead"></div>
    <div class="grid-stats crband" id="crBand"></div>
    <div id="crShoot"></div>
    <div class="ntabs crtabs" id="crTabs"></div>
    <div id="crPanel"></div>
    <p class="crplain" id="crPlain">${esc(COPY.record)}</p>
    <div class="modal" id="crModal"><div class="sheet" id="crSheet"></div></div>`;
  panel = root.querySelector('#crPanel');
  tabsEl = root.querySelector('#crTabs');
  tabsEl.addEventListener('click', (e) => {
    const b = e.target.closest('button[data-tab]');
    if (!b) return;
    S.tab = b.dataset.tab;
    paintTabs();
    paintPanel();
  });
  root.querySelector('#crModal').addEventListener('click', (e) => { if (e.target.id === 'crModal') closeSheet(); });
  // Escape closes the sheet. It hangs off the document rather than off the root
  // that mount() has just replaced, so it is bound once for the life of the
  // page: a second mount would otherwise stack another copy of it.
  if (!escBound) { escBound = true; document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeSheet(); }); }
  // Ratings live on this screen but are another module's file. It hands us one
  // function, renderRatings(rootEl, data, ctx). If the file is not there the
  // tab never appears and nothing else on this page notices.
  import('./ratings.js')
    .then((m) => { if (typeof m?.renderRatings === 'function') { ratingsMod = m; paintTabs(); } })
    .catch(() => {});
  paintTabs();
}

export async function render() {
  if (!root) return;
  // Only the panel is painted while the first call is out. Painting the whole
  // screen would put "Nothing tracked yet" in front of a player with six
  // hundred tracked shots for as long as the fetch takes, and a record that
  // understates what they did is as wrong as one that overstates it.
  if (!S.data) paintPanel();
  await refresh();
}

function url(path) {
  const q = new URLSearchParams();
  q.set('tz', String(new Date().getTimezoneOffset()));
  return `/api/career${path}?${q}`;
}

async function refresh() {
  // Sharing is changed in Settings, so it is read again every time this screen
  // comes back rather than being remembered from the last visit.
  S.orgs = null; S.orgsErr = null;
  try { S.data = await ctx.api(url('')); S.err = null; }
  catch (e) { S.err = e.message; }
  paintAll();
}

/* ------------------------------------------------------------------ paint */

function paintAll() {
  // With nothing back from the Worker the panel carries the screen on its own,
  // as either the loading line or the error. The card, the band and the
  // shooting story stay empty rather than drawing an empty state that tells a
  // player they have done nothing when all that happened is a failed fetch.
  if (!S.data) {
    for (const id of ['#crHead', '#crBand', '#crShoot']) { const el = root?.querySelector(id); if (el) el.innerHTML = ''; }
    paintTabs();
    paintPanel();
    return;
  }
  paintHead();
  paintBand();
  paintShooting();
  paintTabs();
  paintPanel();
  const plain = root?.querySelector('#crPlain');
  if (plain) plain.textContent = copy('record');
}

function paintTabs() {
  if (!tabsEl) return;
  const tabs = ratingsMod ? [...BASE_TABS, ['ratings', 'Ratings']] : BASE_TABS;
  if (!tabs.some(([id]) => id === S.tab)) S.tab = tabs[0][0];
  tabsEl.innerHTML = tabs.map(([id, label]) =>
    `<button type="button" data-tab="${id}" class="${id === S.tab ? 'on' : ''}">${esc(label)}</button>`).join('');
}

function paintPanel() {
  if (!panel) return;
  if (S.err) {
    panel.innerHTML = `<div class="card"><h3>Your record</h3><p class="sub">${esc(S.err)}</p>
      <button class="btn" id="crRetry" style="margin-top:12px">Try again</button></div>`;
    bind('#crRetry', 'click', refresh);
    return;
  }
  if (!S.data) { panel.innerHTML = '<div class="card"><h3>Your record</h3><p class="sub">Loading your record.</p></div>'; return; }
  const fn = { career: paintCareer, games: paintGames, nil: paintNil, share: paintShare, ratings: paintRatings }[S.tab] || paintCareer;
  try {
    const out = fn();
    if (out && typeof out.catch === 'function') out.catch(failed);
  } catch (e) { failed(e); }
}

function failed(e) {
  if (panel) panel.innerHTML = `<div class="card"><h3>Your record</h3><p class="sub">${esc(e?.message || 'That did not load.')}</p></div>`;
}

const bind = (sel, ev, fn, scope = panel) => scope?.querySelectorAll(sel).forEach((el) => el.addEventListener(ev, fn));

/** The tag that says where a number came from. It rides with every number. */
const tag = (source) => (source === 'tracked'
  ? '<span class="crtag measured">Measured</span>'
  : '<span class="crtag logged">Self reported</span>');

/* ============================================================== the card */

function paintHead() {
  const el = root?.querySelector('#crHead');
  if (!el) return;
  const d = data();
  const bio = d.bio || {};
  const settings = ctx?.S?.settings || {};
  const companion = ctx?.companionById?.(settings.companion) || null;
  const name = settings.displayName || 'Your record';
  const current = d.current;
  const height = num(bio.heightCm) ?? num(d.measured?.heightCm?.value);
  const heightFrom = num(bio.heightCm) === null && d.measured?.heightCm ? ' from your health log' : '';
  const weight = num(bio.weightKg) ?? num(d.measured?.weightKg?.value);
  const weightFrom = num(bio.weightKg) === null && d.measured?.weightKg ? ' from your health log' : '';

  const facts = [
    bio.position && ['Position', bio.position],
    height && ['Height', `${fmt1(height)} cm${heightFrom}`],
    weight && ['Weight', `${fmt1(weight)} kg${weightFrom}`],
    bio.dominantHand && ['Shoots', `${cap(bio.dominantHand)} handed`],
    bio.jersey && ['Jersey', `Number ${bio.jersey}`],
    bio.school && ['School', bio.school],
    bio.gradYear && ['Class of', String(bio.gradYear)],
    bio.hometown && ['From', bio.hometown],
  ].filter(Boolean);

  el.innerHTML = `
    <div class="card crcard">
      <div class="crtop">
        <div class="crface">${companion?.image
          ? `<img src="${esc(companion.image)}" width="64" height="64" alt="">`
          : '<span class="crball"></span>'}</div>
        <div class="crwho"><h2>${esc(name)}</h2></div>
        <button class="btn crEdit" id="crEditBio">${d.bio && Object.keys(bio).filter((k) => k !== 'updatedAt').length ? 'Edit card' : 'Fill in your card'}</button>
        <div class="crbadges">
          ${current ? `<span class="pill hot">${esc(levelLabel(current.level))}${current.role ? `, ${esc(current.role)}` : ''}</span>` : ''}
          ${current ? `<span class="pill cool">${esc(current.name)}</span>` : ''}
          ${bio.position ? `<span class="pill">${esc(bio.position)}</span>` : ''}
        </div>
      </div>
      ${facts.length
        ? `<dl class="crfacts">${facts.map(([k, v]) => `<div><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>`).join('')}</dl>`
        : '<p class="sub">Nothing on your card yet. Put your position, school and class year in and it reads like a profile.</p>'}
    </div>`;
  el.querySelector('#crEditBio')?.addEventListener('click', editBio);
}

function paintBand() {
  const el = root?.querySelector('#crBand');
  if (!el) return;
  const t = tracked();
  const g = gameTotals();
  const champs = rows('accolades').filter((a) => a.kind === 'championship').length;
  const teams = rows('teams').length;
  // A tile only appears once there is something real behind it, so a brand new
  // player gets an inviting page instead of a wall of zeros.
  const tiles = [
    ['crm', n0(t.attempts), fmt(t.attempts), 'shots tracked'],
    ['crm', n0(t.sessions), fmt(t.sessions), t.sessions === 1 ? 'session' : 'sessions'],
    ['crm', n0(t.days), fmt(t.days), t.days === 1 ? 'day trained' : 'days trained'],
    ['crl', n0(g.count), fmt(g.count), g.count === 1 ? 'game logged' : 'games logged'],
    ['crl', g.averages?.points ? 1 : 0, g.averages?.points ? fmt1(g.averages.points.value) : '', 'points a game'],
    ['crl', teams, fmt(teams), teams === 1 ? 'team' : 'teams'],
    ['crl', champs, fmt(champs), champs === 1 ? 'championship' : 'championships'],
  ].filter(([, real]) => real > 0);
  // Measured and self reported tiles sit in the same band, so each one says
  // which it is in words. Colour carries it too, and colour on its own is not
  // a label a coach reading this in greyscale or with colour blindness can use.
  el.innerHTML = tiles.map(([cls, , value, label]) =>
    `<div class="stat ${cls}"><b>${value}</b><small>${esc(label)}</small>
      <i class="crsrc">${cls === 'crm' ? 'Measured' : 'Self reported'}</i></div>`).join('');
}

/* =================================================== the shooting story */

function paintShooting() {
  const el = root?.querySelector('#crShoot');
  if (!el) return;
  const t = tracked();
  const line = Array.isArray(data().timeline) ? data().timeline : [];

  if (!t.hasData) {
    el.innerHTML = `<div class="card">
      <div class="crhead"><h3>Shots tracked in the app</h3>${tag('tracked')}</div>
      <p class="sub">Nothing tracked yet. Every shot you take in a session lands here, and this is where the story of yesterday, this month and this year gets written.</p>
      <button class="btn primary" id="crTrain" style="margin-top:14px">Start a session</button>
      <p class="crplain">${esc(copy('measured'))} ${esc(copy('demo'))}</p>
    </div>`;
    el.querySelector('#crTrain')?.addEventListener('click', () => ctx.go('train'));
    return;
  }

  const best = t.best;
  el.innerHTML = `<div class="card">
    <div class="crhead"><h3>Shots tracked in the app</h3>${tag('tracked')}</div>
    <div class="crlines">${line.map(periodRow).join('')}</div>
    ${stripBlock(t)}
    <div class="crsplit">
      ${best ? `<div class="crnote"><b>Biggest session</b><p>${fmt(best.attempts)} shots on ${esc(dayLabel(dayOfIso(best.at)))}${
        best.judged ? `, ${fmt(best.makes)} of ${fmt(best.judged)} judged went in` : ', none of them judged made or missed'}.</p></div>` : ''}
      <div class="crnote"><b>First tracked</b><p>${t.first ? esc(fmtDay(dayOfIso(t.first))) : 'Not yet'}${
        t.last ? `. Last tracked ${esc(dayLabel(dayOfIso(t.last)))}.` : '.'}</p></div>
    </div>
    <p class="crplain">${esc(copy('measured'))} A shot counts as an attempt whether or not anybody said it went in, so makes are shown out of the ones that were judged.</p>
  </div>`;
}

/** The dates a period covers, in plain words. The label already says the rest. */
function periodWhen(p) {
  const raw = String(p.when || '');
  if (/^\d{4}-\d{2}-\d{2} to \d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [from, to] = raw.split(' to ');
    return `${shortDay(from)} to ${shortDay(to)}`;
  }
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? shortDay(raw) : '';
}

function periodRow(p) {
  const when = periodWhen(p);
  const made = p.judged ? `${fmt(p.makes)} of ${fmt(p.judged)} judged went in` : 'none judged made or missed';
  return `<div class="crline ${p.attempts ? '' : 'off'}">
    <span class="crline-l"><b>${esc(p.label)}</b>${when ? `<small>${esc(when)}</small>` : ''}</span>
    ${p.attempts
      ? `<span class="crline-v"><b>${fmt(p.attempts)}</b> shot${p.attempts === 1 ? '' : 's'}</span>
         <span class="crline-m">${esc(made)}${p.sessions ? ` &middot; ${fmt(p.sessions)} session${p.sessions === 1 ? '' : 's'}` : ''}</span>`
      : '<span class="crline-v crnil">Nothing logged</span><span class="crline-m"></span>'}
  </div>`;
}

/** A bar a day for the last thirty. A day nobody trained is an empty slot. */
function stripBlock(t) {
  const strip = Array.isArray(t.strip) ? t.strip : [];
  if (!strip.length) return '';
  // The tallest day, and whether there is one at all. A player whose last
  // session was six weeks ago has thirty empty days, and the scale that draws
  // them must not turn into a "most in a day" they never shot.
  const most = Math.max(...strip.map((d) => n0(d.attempts)), 0);
  const top = most || 1;
  const bars = strip.map((d) => {
    const a = n0(d.attempts);
    const h = a ? Math.max(6, Math.round((a / top) * 100)) : 0;
    const title = a ? `${dayLabel(d.date)}: ${a} shot${a === 1 ? '' : 's'}` : `${dayLabel(d.date)}: nothing tracked`;
    return `<span class="crbar${a ? '' : ' off'}" title="${esc(title)}"><i style="height:${h}%"></i></span>`;
  }).join('');
  return `<div class="crstripwrap">
    <div class="crstrip" role="img" aria-label="Shots tracked on each of the last ${strip.length} days">${bars}</div>
    <div class="crends"><span>${esc(shortDay(strip[0].date))}</span>
      <span>${most ? `Most in a day: ${fmt(most)}` : `Nothing in the last ${strip.length} days`}</span>
      <span>Today</span></div>
  </div>`;
}

/* ============================================== 1. the career timeline */

function paintCareer() {
  const teams = rows('teams');
  const milestones = rows('milestones');
  const accolades = rows('accolades');
  const spine = [
    ...teams.map((x) => ({ ...x, group: 'teams' })),
    ...milestones.map((x) => ({ ...x, group: 'milestones' })),
    ...accolades.map((x) => ({ ...x, group: 'accolades' })),
  ].sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));

  panel.innerHTML = `
    <div class="card">
      <div class="crhead"><h3>Career timeline</h3>${tag('logged')}</div>
      <div class="crlist">
        <button class="btn" data-add="teams">Add a team</button>
        <button class="btn" data-add="milestones">Add a milestone</button>
        <button class="btn" data-add="accolades">Add a championship or award</button>
      </div>
      ${spine.length ? `<div class="crspine">${spine.map(spineRow).join('')}</div>` : `
        <p class="sub" style="margin-top:16px">Nothing on the spine yet. Teams you have played for, the day you made your first roster, the championships you won: they all sit here, newest first.</p>`}
      <p class="crplain">${esc(copy('logged'))}</p>
    </div>`;
  bind('[data-add]', 'click', (e) => addTo(e.currentTarget.dataset.add));
  bind('.crspine button[data-id]', 'click', (e) => {
    const { group, id } = e.currentTarget.dataset;
    editIn(group, rows(group).find((x) => String(x.id) === id));
  });
}

function spineRow(x) {
  // The season is free text the player typed, and it goes in as markup, so it
  // is escaped here rather than at the end: the separator is a real entity and
  // escaping the joined string would print it.
  const when = x.group === 'teams'
    ? [x.season && esc(x.season), x.from && `${esc(shortDay(x.from))}${x.to ? ` to ${esc(shortDay(x.to))}` : ''}`].filter(Boolean).join(' &middot; ')
    : esc(fmtDay(x.at));
  const title = x.group === 'teams' ? x.name : x.title;
  const meta = x.group === 'teams'
    ? [levelLabel(x.level), x.role].filter(Boolean).join(', ')
    : [x.kind && cap(String(x.kind).replace(/-/g, ' ')), x.team].filter(Boolean).join(', ');
  return `<button type="button" class="crevent cr-${esc(x.group)}" data-group="${esc(x.group)}" data-id="${esc(x.id)}">
    <span class="crdot"></span>
    <span class="crevent-b">
      <b>${esc(title || 'Untitled')}</b>
      ${meta ? `<em>${esc(meta)}</em>` : ''}
      ${x.note ? `<span class="crevent-n">${esc(x.note)}</span>` : ''}
    </span>
    <span class="crevent-w">${when || ''}</span>
  </button>`;
}

/* ==================================================== 2. the games table */

const COLS = [
  ['at', 'Date', 'text'], ['opponent', 'Opponent', 'text'], ['result', 'Result', 'text'],
  ['minutes', 'MIN', 'n'], ['points', 'PTS', 'n'], ['rebounds', 'REB', 'n'], ['assists', 'AST', 'n'],
  ['steals', 'STL', 'n'], ['blocks', 'BLK', 'n'], ['turnovers', 'TO', 'n'],
  ['fg', 'FG', 'pair'], ['three', '3P', 'pair'], ['ft', 'FT', 'pair'],
];

function paintGames() {
  const games = rows('games');
  const g = gameTotals();
  const sorted = sortGames(games);

  panel.innerHTML = `
    <div class="card">
      <div class="crhead"><h3>Games</h3>${tag('logged')}</div>
      ${g.hasData ? gameAverages(g) : ''}
      <div class="crlist"><button class="btn primary" id="crAddGame">Log a game</button></div>
      ${games.length ? `
        <div class="crscroll">
          <table class="crtable">
            <thead><tr>${COLS.map(([key, label]) => `
              <th${S.sort.key === key ? ` aria-sort="${S.sort.dir < 0 ? 'descending' : 'ascending'}"` : ''}>
                <button type="button" data-sort="${key}" class="${S.sort.key === key ? 'on' : ''}">${esc(label)}</button>
              </th>`).join('')}</tr></thead>
            <tbody>${sorted.map(gameRow).join('')}</tbody>
          </table>
        </div>
        <p class="sub" style="margin-top:12px">Tap a row to edit or delete it. A blank cell is a number nobody put in, not a zero.</p>`
        : '<p class="sub" style="margin-top:16px">No games logged yet. Put in a box score after your next game and your totals, averages and career highs build themselves from it.</p>'}
      <p class="crplain">${esc(copy('logged'))}</p>
    </div>`;
  bind('#crAddGame', 'click', () => addTo('games'));
  bind('[data-sort]', 'click', (e) => {
    const key = e.currentTarget.dataset.sort;
    S.sort = { key, dir: S.sort.key === key ? -S.sort.dir : -1 };
    paintPanel();
  });
  const openRow = (el) => editIn('games', rows('games').find((x) => String(x.id) === el.dataset.id));
  bind('tr[data-id]', 'click', (e) => openRow(e.currentTarget));
  // The rows take focus, so they have to open on a key as well as on a tap.
  bind('tr[data-id]', 'keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    openRow(e.currentTarget);
  });
}

function gameAverages(g) {
  const avg = (key, label) => {
    const a = g.averages?.[key];
    return a ? `<div class="stat crl"><b>${fmt1(a.value)}</b><small>${esc(label)} in ${fmt(a.games)} game${a.games === 1 ? '' : 's'}</small></div>` : '';
  };
  const split = (s, label) => (s && s.att
    ? `<div class="stat crl"><b>${fmt(s.pct)}%</b><small>${esc(label)} ${fmt(s.made)} of ${fmt(s.att)}</small></div>` : '');
  const high = (key, label) => {
    const h = g.highs?.[key];
    return h ? `<div class="crnote"><b>Best ${esc(label)}</b><p>${fmt(h.value)}${h.opponent ? ` against ${esc(h.opponent)}` : ''}${h.at ? ` on ${esc(fmtDay(h.at))}` : ''}.</p></div>` : '';
  };
  const rec = g.record || {};
  return `
    <div class="grid-stats crband">
      ${avg('points', 'points')}${avg('rebounds', 'rebounds')}${avg('assists', 'assists')}
      ${avg('steals', 'steals')}${avg('blocks', 'blocks')}${avg('minutes', 'minutes')}
      ${split(g.splits?.fg, 'field goals')}${split(g.splits?.three, 'from three')}${split(g.splits?.ft, 'free throws')}
      ${rec.logged ? `<div class="stat crl"><b>${fmt(rec.win)}&ndash;${fmt(rec.loss)}${rec.tie ? `&ndash;${fmt(rec.tie)}` : ''}</b><small>in ${fmt(rec.logged)} game${rec.logged === 1 ? '' : 's'} with a result</small></div>` : ''}
    </div>
    <div class="crsplit">${high('points', 'scoring game')}${high('rebounds', 'rebounding game')}${high('assists', 'passing game')}</div>
    <p class="sub">An average is worked out over the games that actually carry that number, and the count says how many that was.</p>`;
}

function sortGames(games) {
  const { key, dir } = S.sort;
  const val = (g) => {
    if (key === 'fg') return num(g.fgMade);
    if (key === 'three') return num(g.threeMade);
    if (key === 'ft') return num(g.ftMade);
    return g[key] === undefined || g[key] === null || g[key] === '' ? null : g[key];
  };
  return [...games].sort((a, b) => {
    const x = val(a), y = val(b);
    // A number nobody entered always sits at the bottom, whichever way the
    // column is sorted, so a blank never reads as a zero.
    if (x === null && y === null) return String(b.at || '').localeCompare(String(a.at || ''));
    if (x === null) return 1;
    if (y === null) return -1;
    const cmp = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return cmp * dir;
  });
}

function gameRow(g) {
  const cell = (key, kind) => {
    if (kind === 'pair') {
      const made = num(g[`${key}Made`]), att = num(g[`${key}Att`]);
      return made === null && att === null ? '<td class="crnil">&ndash;</td>' : `<td>${made === null ? '?' : fmt(made)} of ${att === null ? '?' : fmt(att)}</td>`;
    }
    if (key === 'at') return `<td class="crw">${esc(fmtDay(g.at))}</td>`;
    if (key === 'opponent') return `<td class="crw">${esc(g.opponent || '')}</td>`;
    if (key === 'result') return g.result ? `<td><span class="pill ${g.result === 'win' ? 'good' : g.result === 'loss' ? 'hot' : 'cool'}">${esc(cap(g.result))}</span></td>` : '<td class="crnil">&ndash;</td>';
    const v = num(g[key]);
    return v === null ? '<td class="crnil">&ndash;</td>' : `<td>${fmt(v)}</td>`;
  };
  return `<tr data-id="${esc(g.id)}" tabindex="0">${COLS.map(([key, , kind]) => cell(key, kind)).join('')}</tr>`;
}

/* =========================================================== 3. NIL */

function paintNil() {
  const deals = rows('nil');
  panel.innerHTML = `
    <div class="card">
      <div class="crhead"><h3>NIL</h3>${tag('logged')}</div>
      <p class="sub">${esc(copy('nil'))}</p>
      <div class="crlist"><button class="btn" id="crAddNil">Record a deal</button></div>
      ${deals.length ? `<div class="crspine">${deals.map((x) => `
        <button type="button" class="crevent cr-nil" data-id="${esc(x.id)}">
          <span class="crdot"></span>
          <span class="crevent-b">
            <b>${esc(x.brand)}</b>
            ${x.kind ? `<em>${esc(cap(String(x.kind)))}</em>` : ''}
            ${x.note ? `<span class="crevent-n">${esc(x.note)}</span>` : ''}
          </span>
          <span class="crevent-w">${esc(fmtDay(x.at))}</span>
        </button>`).join('')}</div>`
        : '<p class="sub" style="margin-top:16px">Nothing recorded. If you ever do a deal, keep a note of who it was with and when, in your own words.</p>'}
    </div>`;
  bind('#crAddNil', 'click', () => addTo('nil'));
  bind('.crevent[data-id]', 'click', (e) => editIn('nil', rows('nil').find((x) => String(x.id) === e.currentTarget.dataset.id)));
}

/* ======================================================= 4. sharing */

async function paintShare() {
  panel.innerHTML = `<div class="card"><h3>Sharing</h3><p class="sub">Checking who you share with.</p></div>`;
  if (!S.orgs && !S.orgsErr) {
    try { S.orgs = await ctx.api('/api/orgs'); S.orgsErr = null; }
    catch (e) { S.orgs = null; S.orgsErr = e.message; }
  }
  if (S.tab !== 'share' || !panel) return;
  const orgs = Array.isArray(S.orgs?.orgs) ? S.orgs.orgs : [];
  const shares = Array.isArray(S.orgs?.shares) ? S.orgs.shares : [];
  const shareFor = (id) => shares.find((s) => s.orgId === id && new Date(s.until) > new Date());
  // Sharing is per scope, and 'career' is a scope worker/orgs.js has to carry
  // before a coach can open this page at all. Until a share actually names it,
  // the honest answer to "who can read my record" is nobody, so that is what
  // this says rather than implying the whole page is already out there.
  const readsRecord = (sh) => Array.isArray(sh?.scopes) && sh.scopes.includes('career');
  panel.innerHTML = `
    <div class="card">
      <h3>Sharing</h3>
      <p class="sub">${esc(copy('sharing'))}</p>
      ${S.orgsErr
        ? `<p class="sub" style="margin-top:14px">You are not signed in, so there is nobody to share with yet. Sign in from Settings and a family or team can be added there.</p>`
        : orgs.length
          ? `<div class="crshares">${orgs.map((o) => {
              const sh = shareFor(o.id);
              return `<div class="crshare">
                <div><b>${esc(o.name)}</b><small>${esc(cap(o.type))}${o.myRole ? `, you are the ${esc(o.myRole)}` : ''}</small></div>
                <div class="crpills">${sh
                  ? `<span class="pill ${readsRecord(sh) ? 'good' : ''}">${readsRecord(sh) ? 'Reads this record' : 'Not this record'}</span>
                     <span class="pill">Sharing ${esc(sh.scopes.join(', '))} until ${esc(fmtDay(String(sh.until).slice(0, 10)))}</span>`
                  : '<span class="pill">Not sharing</span>'}</div>
              </div>`;
            }).join('')}</div>`
          : '<p class="sub" style="margin-top:14px">You are not in a family or a team yet. One can be set up in Settings, and whoever runs it invites you.</p>'}
      <div class="crlist"><button class="btn" id="crGoSettings">Open Settings</button></div>
      <p class="crplain">There is no public link to this page and there never will be one. Nobody reads it unless you share with a group you are in.</p>
    </div>`;
  bind('#crGoSettings', 'click', () => ctx.go('settings'));
}

/* ======================================================= 5. ratings */

function paintRatings() {
  if (!ratingsMod) { panel.innerHTML = '<div class="card"><h3>Ratings</h3><p class="sub">Ratings are not switched on yet.</p></div>'; return; }
  panel.innerHTML = '<div id="careerRatingsRoot"></div>';
  // It fetches its own data when we pass null, and it never throws.
  return Promise.resolve(ratingsMod.renderRatings(panel.querySelector('#careerRatingsRoot'), null, ctx));
}

/* ============================================================ the sheet */

function closeSheet() { root?.querySelector('#crModal')?.classList.remove('open'); }

/** Every form on this screen: which fields, and what the Worker calls them. */
function fieldsFor(group) {
  const r = ranges();
  const n = (key, label) => ({ key, label: label || r[key]?.label || key, type: 'number', min: r[key]?.min, max: r[key]?.max, step: r[key]?.step, unit: r[key]?.unit });
  const kinds = data().kinds || {};
  if (group === 'teams') return [
    { key: 'name', label: 'Team', type: 'text', maxLength: 80, wide: true, required: true },
    { key: 'level', label: 'Level', type: 'select', options: levels().map((l) => [l.id, l.label]), required: true },
    { key: 'role', label: 'Your role', type: 'text', maxLength: 40, placeholder: 'Starting guard' },
    { key: 'season', label: 'Season', type: 'text', maxLength: 30, placeholder: '2026 spring' },
    { key: 'from', label: 'Started', type: 'date' },
    { key: 'to', label: 'Finished', type: 'date' },
    { key: 'note', label: 'Note', type: 'text', maxLength: 300, wide: true },
  ];
  if (group === 'milestones' || group === 'accolades') return [
    { key: 'title', label: group === 'teams' ? 'Title' : 'What happened', type: 'text', maxLength: 120, wide: true, required: true },
    { key: 'at', label: 'When', type: 'date', required: true },
    { key: 'kind', label: 'Kind', type: 'select', options: (kinds[group === 'milestones' ? 'milestone' : 'accolade'] || []).map((k) => [k, cap(k.replace(/-/g, ' '))]) },
    { key: 'team', label: 'Team', type: 'text', maxLength: 80 },
    { key: 'note', label: 'Note', type: 'text', maxLength: 400, wide: true },
  ];
  if (group === 'nil') return [
    { key: 'brand', label: 'Who it is with', type: 'text', maxLength: 80, wide: true, required: true },
    { key: 'at', label: 'When you agreed it', type: 'date', required: true },
    { key: 'kind', label: 'Kind', type: 'select', options: (kinds.nil || []).map((k) => [k, cap(k)]) },
    { key: 'note', label: 'What it was', type: 'text', maxLength: 400, wide: true, placeholder: 'Two pairs of shoes for a photo' },
  ];
  if (group === 'games') return [
    { key: 'at', label: 'Date', type: 'date', required: true },
    { key: 'opponent', label: 'Opponent', type: 'text', maxLength: 80 },
    { key: 'team', label: 'Your team', type: 'text', maxLength: 80 },
    { key: 'result', label: 'Result', type: 'select', options: (data().results || []).map((x) => [x, cap(x)]) },
    n('minutes'), n('points'), n('rebounds'), n('assists'), n('steals'), n('blocks'), n('turnovers'),
    n('fgMade'), n('fgAtt'), n('threeMade'), n('threeAtt'), n('ftMade'), n('ftAtt'),
    { key: 'note', label: 'Note', type: 'text', maxLength: 400, wide: true },
  ];
  return [];
}

const TITLES = {
  teams: ['Add a team', 'Edit this team'],
  milestones: ['Add a milestone', 'Edit this milestone'],
  accolades: ['Add a championship or award', 'Edit this one'],
  nil: ['Record a deal', 'Edit this record'],
  games: ['Log a game', 'Edit this game'],
};
const SHEET_NOTE = {
  games: 'Only what you put in is counted. Leave a box empty and it stays out of your totals rather than counting as a zero.',
  nil: 'A record you keep, in your own words. No amounts, no terms, and no advice from us.',
  teams: 'Your most recent team is the one shown at the top of your card.',
  milestones: 'The first roster you made, the league you joined, the day you moved up.',
  accolades: 'Championships, awards, all star selections, records.',
};

const addTo = (group) => openForm(group, null);
const editIn = (group, record) => { if (record) openForm(group, record); };

function openForm(group, record) {
  const sheet = root?.querySelector('#crSheet');
  if (!sheet) return;
  const fields = fieldsFor(group);
  const [addTitle, editTitle] = TITLES[group] || ['Add', 'Edit'];

  sheet.innerHTML = `
    <h2>${esc(record ? editTitle : addTitle)}</h2>
    <p class="sub">${esc(SHEET_NOTE[group] || '')}</p>
    <div class="crform">${fields.map((f) => fieldHtml(f, record)).join('')}</div>
    <div class="cractions">
      <button class="btn primary" id="crSave">${record ? 'Save changes' : 'Save'}</button>
      <button class="btn" id="crCancel">Cancel</button>
      ${record ? '<button class="btn" id="crDelete" style="color:var(--bad)">Delete</button>' : ''}
    </div>
    <p class="crplain">${esc(copy('logged'))}</p>`;
  root.querySelector('#crModal').classList.add('open');
  sheet.querySelector('input,select')?.focus();

  sheet.querySelector('#crCancel').addEventListener('click', closeSheet);
  sheet.querySelector('#crSave').addEventListener('click', async () => {
    const body = readForm(sheet, fields, record);
    const btns = sheet.querySelectorAll('button');
    btns.forEach((b) => { b.disabled = true; });
    try {
      await ctx.api(url(record ? `/${group}/${encodeURIComponent(record.id)}` : `/${group}`), { method: record ? 'PATCH' : 'POST', body: JSON.stringify(body) });
      closeSheet();
      ctx.toast(record ? 'Saved.' : 'Added.');
      await refresh();
    } catch (e) {
      ctx.toast(e.message, true);
      btns.forEach((b) => { b.disabled = false; });
    }
  });
  sheet.querySelector('#crDelete')?.addEventListener('click', async () => {
    try {
      const ok = await ctx.ui.confirm({
        title: 'Take this off your record?',
        body: 'It comes off your timeline and out of every total on this page.',
        confirmText: 'Delete', danger: true,
      });
      if (!ok) return;
      await ctx.api(url(`/${group}/${encodeURIComponent(record.id)}`), { method: 'DELETE' });
      closeSheet();
      ctx.toast('Taken off.');
      await refresh();
    } catch (e) { ctx.toast(e.message, true); }
  });
}

function fieldHtml(f, record) {
  const value = record && record[f.key] != null ? String(record[f.key]) : (f.type === 'date' && !record ? todayKey() : '');
  const id = `crf_${f.key}`;
  const label = `${esc(f.label)}${f.unit ? ` <em>${esc(f.unit)}</em>` : ''}${f.required ? ' <em>needed</em>' : ''}`;
  if (f.type === 'select') {
    const opts = [['', f.required ? 'Pick one' : 'Not said'], ...(f.options || [])];
    return `<label class="crlab${f.wide ? ' wide' : ''}" for="${id}">${label}
      <select id="${id}" data-f="${esc(f.key)}">${opts.map(([v, l]) =>
        `<option value="${esc(v)}" ${String(value) === String(v) ? 'selected' : ''}>${esc(l)}</option>`).join('')}</select></label>`;
  }
  const attrs = f.type === 'number'
    ? `type="number" inputmode="decimal" min="${f.min ?? 0}" max="${f.max ?? 999}" step="${f.step ?? 1}"`
    : f.type === 'date' ? 'type="date"' : `type="text" maxlength="${f.maxLength || 100}"`;
  return `<label class="crlab${f.wide ? ' wide' : ''}" for="${id}">${label}
    <input id="${id}" data-f="${esc(f.key)}" ${attrs} placeholder="${esc(f.placeholder || '')}" value="${esc(value)}"></label>`;
}

/**
 * What goes up. On an edit every box is sent, so clearing one takes that value
 * off the record. On a new one an empty box is left out entirely, so nothing
 * lands as a zero that was never typed.
 */
function readForm(sheet, fields, record) {
  const body = {};
  for (const f of fields) {
    const el = sheet.querySelector(`[data-f="${f.key}"]`);
    if (!el) continue;
    const raw = String(el.value ?? '').trim();
    if (raw === '') { if (record) body[f.key] = null; continue; }
    body[f.key] = f.type === 'number' ? Number(raw) : raw;
  }
  return body;
}

/* --------------------------------------------------------------- the card */

function editBio() {
  const sheet = root?.querySelector('#crSheet');
  if (!sheet) return;
  const bio = data().bio || {};
  const r = ranges();
  const fields = [
    { key: 'position', label: 'Position', type: 'text', maxLength: 40, placeholder: 'Guard' },
    { key: 'jersey', label: 'Jersey number', type: 'text', maxLength: 3, placeholder: '00' },
    { key: 'dominantHand', label: 'Shooting hand', type: 'select', options: (data().hands || []).map((h) => [h, cap(h)]) },
    { key: 'heightCm', label: r.heightCm?.label || 'Height', type: 'number', min: r.heightCm?.min, max: r.heightCm?.max, step: r.heightCm?.step, unit: 'cm' },
    { key: 'weightKg', label: r.weightKg?.label || 'Weight', type: 'number', min: r.weightKg?.min, max: r.weightKg?.max, step: r.weightKg?.step, unit: 'kg' },
    { key: 'school', label: 'School', type: 'text', maxLength: 80 },
    { key: 'gradYear', label: 'Class year', type: 'number', min: r.gradYear?.min, max: r.gradYear?.max, step: 1 },
    { key: 'hometown', label: 'Hometown', type: 'text', maxLength: 80 },
  ];
  sheet.innerHTML = `
    <h2>Your card</h2>
    <p class="sub">All of it is optional. Height and weight fill themselves in from your health log if you have weighed in there, and putting them here overrides that.</p>
    <div class="crform">${fields.map((f) => fieldHtml(f, bio)).join('')}</div>
    <div class="cractions">
      <button class="btn primary" id="crSave">Save card</button>
      <button class="btn" id="crCancel">Cancel</button>
    </div>
    <p class="crplain">${esc(copy('record'))}</p>`;
  root.querySelector('#crModal').classList.add('open');
  sheet.querySelector('#crCancel').addEventListener('click', closeSheet);
  sheet.querySelector('#crSave').addEventListener('click', async () => {
    const body = readForm(sheet, fields, bio);
    // Every box on the card is sent, so emptying one clears it.
    for (const f of fields) if (!(f.key in body)) body[f.key] = null;
    const btns = sheet.querySelectorAll('button');
    btns.forEach((b) => { b.disabled = true; });
    try {
      await ctx.api(url('/bio'), { method: 'PATCH', body: JSON.stringify(body) });
      closeSheet();
      ctx.toast('Card saved.');
      await refresh();
    } catch (e) {
      ctx.toast(e.message, true);
      btns.forEach((b) => { b.disabled = false; });
    }
  });
}
